import _ from "lodash";
import { useCallback, useReducer } from "react";
import { IAttributeValueOption } from "../../../services/attribute-data-service/Models";
import { ViewOptionStateEnum } from "../../contextual-app-bar/ViewOptionStateEnum";


export enum AttributeSchemaObjectTabEnum {
    None = -1,
    Devices = 0,
    Wells = 1,
    TankBatteries = 2,
    Tanks = 3
}

export type AttributeSchemaObjectStateType = {
    activeTab: AttributeSchemaObjectTabEnum;
    objectNameFilter: string;
    viewSelected: boolean;
    viewOptionState: ViewOptionStateEnum;
    valueOptionFilter: IAttributeValueOption[];
};

enum AttributeSchemaObjectActionType {
    ActiveTabChanged = "ACTIVE_TAB_CHANGED",
    ObjectFilterChanged = "OBJECT_FILTER_CHANGED",
    ViewSelectedChanged = "VIEW_SELECTED_CHANGED",
    ValueOptionFilterChanged = "VALUE_OPTION_FILTER_CHANGED",
    ResetChanged = "RESET_CHANGED",
}

type AttributeSchemaObjectAction = {
    type: AttributeSchemaObjectActionType.ActiveTabChanged;
    activeTab: AttributeSchemaObjectTabEnum;
} | {
     type: AttributeSchemaObjectActionType.ObjectFilterChanged;
     payload: string;
} | {
    type: AttributeSchemaObjectActionType.ViewSelectedChanged;
    payload: boolean;
} | {
    type: AttributeSchemaObjectActionType.ValueOptionFilterChanged;
    payload: IAttributeValueOption[];
} | {
    type: AttributeSchemaObjectActionType.ResetChanged;
    payload: void;
};

const initialState: AttributeSchemaObjectStateType = {
    activeTab: AttributeSchemaObjectTabEnum.None,
    objectNameFilter: '',
    viewSelected: false,
    viewOptionState: ViewOptionStateEnum.Default,
    valueOptionFilter: []
};

const optionsChanged = (newState: AttributeSchemaObjectStateType, 
    defaultState: AttributeSchemaObjectStateType) => 
    newState.objectNameFilter !== defaultState.objectNameFilter ||
    newState.viewSelected !== defaultState.viewSelected ||
    newState.valueOptionFilter.length > 0;

function attributeSchemaObjectReducer(state: AttributeSchemaObjectStateType, action: AttributeSchemaObjectAction): AttributeSchemaObjectStateType {
    let copy = _.cloneDeep(state);    

    switch (action.type) {

        case AttributeSchemaObjectActionType.ActiveTabChanged:
            copy.activeTab = action.activeTab;
            break;

        case AttributeSchemaObjectActionType.ObjectFilterChanged:
            copy.objectNameFilter = action.payload;
            break;

        case AttributeSchemaObjectActionType.ViewSelectedChanged:
            copy.viewSelected = action.payload;
            break;

        case AttributeSchemaObjectActionType.ValueOptionFilterChanged:
            copy.valueOptionFilter = action.payload;
            break;

        case AttributeSchemaObjectActionType.ResetChanged:
            copy = _.cloneDeep(initialState);
            break;

        default:
            return state;
    }

    copy.viewOptionState = optionsChanged(copy, initialState) ? 
                            ViewOptionStateEnum.Changed : 
                            ViewOptionStateEnum.Default;

    return copy;
}

export type UseAttributeSchemaObjectStateType = {
    state: AttributeSchemaObjectStateType;
    setActiveTab: (activeTab: AttributeSchemaObjectTabEnum) => void;
    setObjectFilter: (value: string) => void;
    setViewSelected: (value: boolean) => void;
    setValueFilter: (value: IAttributeValueOption[]) => void;
    resetState: () => void;
};

export function useAttributeSchemaObjectState(): UseAttributeSchemaObjectStateType {

    const [state, dispatch] = useReducer(attributeSchemaObjectReducer, initialState);

    const setActiveTab = useCallback((activeTab: AttributeSchemaObjectTabEnum) => dispatch({
        type: AttributeSchemaObjectActionType.ActiveTabChanged,
        activeTab: activeTab
    }), []);

    const setObjectFilter = useCallback((value: string) => dispatch({
        type: AttributeSchemaObjectActionType.ObjectFilterChanged,
        payload: value
    }), []);


    const setViewSelected = useCallback((value: boolean) => dispatch({
        type: AttributeSchemaObjectActionType.ViewSelectedChanged,
        payload: value
    }), []);

    const setValueFilter = useCallback((value: IAttributeValueOption[]) => dispatch({
        type: AttributeSchemaObjectActionType.ValueOptionFilterChanged,
        payload: value
    }), []);


    const resetState = useCallback(() => dispatch({
        type: AttributeSchemaObjectActionType.ResetChanged,
        payload: undefined
    }), [])

    return {
        state,
        setActiveTab,
        setObjectFilter,
        setViewSelected,
        setValueFilter,
        resetState
    };
}
